import { EventEmitter, Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Table } from '../entities/Table';
import { Tables } from '../entities/tables';
import { ViewDto } from '../entities/viewdto';
import { Language } from '../entities/language';

@Injectable({
    providedIn: 'root',
})
export class TabService {
    public dataSourceSubject = new BehaviorSubject<{
        [entitteName: string]: MatTableDataSource<any>;
    }>({});
    defaultTab: Tables = new Tables();
    public datasourceUpdated: EventEmitter<any> = new EventEmitter();
    private deleteSubject = new Subject<number>();
    private updateDataSubject = new Subject<number>();
    private updatedashboardSubject = new Subject<{ tabId: any; data: any }>();
    private updateTemplateSubject = new Subject<{ tabId: any; data: any }>();
    private editDataTempByTav = new BehaviorSubject<any>({});

    selectedValue: any;
    // private updateTemplateSubject = new Subject<any>();

    get deleteEvent(): Observable<number> {
        return this.deleteSubject.asObservable();
    }

    get updateDataEvent(): Observable<number> {
        return this.updateDataSubject.asObservable();
    }

    // get updateTemplateEvent(): Observable<any> {
    //   return this.updateTemplateSubject.asObservable();
    // }
    public tabs: Tables[] = [];

    constructor() {
        this.defaultTab.entite = 'accueil';
        this.defaultTab.title = 'Accueil';
        this.tabs.push(this.defaultTab);
    }

    get updateTemplateEvent(): Observable<{ tabId: any; data: any }> {
        return this.updateTemplateSubject.asObservable();
    }

    setDataSource(entiteName: string, value: MatTableDataSource<any>): void {
        const currentDataSource = this.dataSourceSubject.value;
        currentDataSource[entiteName] = value;
        this.dataSourceSubject.next(currentDataSource);
    }

    getDataSource(entiteName: string): MatTableDataSource<any> {
        return this.dataSourceSubject.value[entiteName];
    }

    deleteListItems(itemIds: string[], key: string, totalElements: number) {
        const currentData = this.dataSourceSubject.value[key].data;
        const newData = currentData.filter((item) => !itemIds.includes(item.id));
        this.dataSourceSubject.value[key].data = newData;
        let newTotalElements = totalElements - itemIds.length;
        this.deleteSubject.next(newTotalElements);
    }
    updateOrAddItem(item: any, key: string): void {
        const dataSourceValue = this.dataSourceSubject.value;
        if (!dataSourceValue || !dataSourceValue[key] || !dataSourceValue[key].data) {
            return;
        }

        const currentData = dataSourceValue[key].data;
        const index = currentData.findIndex((x) => x.id === item.id);

        if (index === -1) {
            // Add the item as the first element in the array
            currentData.unshift(item);
        } else {
            currentData[index] = item;
        }

        dataSourceValue[key].data = currentData;
        let newTotalElements = currentData.length;
        this.updateDataSubject.next(newTotalElements);
    }

    // updateTemplateData(data: any): void {
    //   // Emit a new object instead of updating the same reference
    //   this.updateTemplateSubject.next(Object.assign({}, data));
    // }
    updateTemplateData(tabId: any, data: any): void {
        // Emit a new object with tabId and data properties
        this.updateTemplateSubject.next({ tabId, data });
    }

    getTabTitle(obj: any, languages: Language[], entite: any): any | undefined {
        if (languages && languages.length > 0) {
            this.selectedValue = languages
                .filter((language) => language.selected === true)
                .map((language) => language.key);
        }

        if (entite?.toLocaleLowerCase() === 'menu') {
            return obj['text'];
        } else if (
            entite?.toLocaleLowerCase() === 'produit' ||
            entite?.toLocaleLowerCase() === 'categorie'
        ) {
            return obj['code'];
        } else if (entite === 'variablelogistique') {
            return obj['mapDescriptionVl'][this.selectedValue[0]];
        } else if (entite?.toLocaleLowerCase() === 'picto') {
            if (this.selectedValue[0]) {
                return obj['label'][this.selectedValue[0]];
            } else return obj['label'].fr_FR;
        } else if (entite?.toLocaleLowerCase() === 'pictocatalogue') {
            return obj['famille'];
        } else if (entite.toLocaleLowerCase() === 'parametre') {
            return obj['valeur'];
        } else if (
            entite?.toLocaleLowerCase() === 'role' ||
            entite?.toLocaleLowerCase() === 'user'
        ) {
            return obj['nom'];
        }
    }

    saveEditData(tabId: string, data: any, isDelete?: boolean, tabIndex?: number) {
        // Use tabId-tabIndex as key to store data for each tab within the same record
        const key = tabIndex !== undefined ? `${tabId}-${tabIndex}` : tabId;

        if (isDelete) {
            const updateData = { ...this.editDataTempByTav.value };
            delete updateData[key];
            // Also delete all tab-specific data for this tabId if deleting the entire record
            if (tabIndex === undefined) {
                Object.keys(updateData).forEach((k) => {
                    if (k.startsWith(`${tabId}-`)) {
                        delete updateData[k];
                    }
                });
            }
            this.editDataTempByTav.next(updateData);
        } else {
            const updateData = { ...this.editDataTempByTav.value, [key]: data };
            this.editDataTempByTav.next(updateData);
        }
    }

    getTempEditData(tabId: string, tabIndex?: number) {
        // Get data for specific tab index, or fallback to tabId only
        if (tabIndex !== undefined) {
            const key = `${tabId}-${tabIndex}`;
            return (
                this.editDataTempByTav.value?.[key] ?? this.editDataTempByTav.value?.[tabId] ?? null
            );
        }
        return this.editDataTempByTav.value?.[tabId] ?? null;
    }

    /**
     * Get all temporary edit data for a record (from all tabs)
     * Merges data from all tab indices into a single object
     */
    getAllTempEditData(tabId: string): any {
        const allData: any = {};
        const editData = this.editDataTempByTav.value || {};

        // Get data from tab-specific keys (tabId-tabIndex)
        Object.keys(editData).forEach((key) => {
            if (key.startsWith(`${tabId}-`)) {
                const tabData = editData[key];
                if (tabData) {
                    Object.assign(allData, tabData);
                }
            }
        });

        // Also include data from tabId key if exists (backward compatibility)
        if (editData[tabId]) {
            Object.assign(allData, editData[tabId]);
        }

        return Object.keys(allData).length > 0 ? allData : null;
    }

    /**
     * Clear all temporary edit data for a record
     */
    clearTempEditData(tabId: string) {
        const updateData = { ...this.editDataTempByTav.value };

        // Delete all tab-specific data
        Object.keys(updateData).forEach((k) => {
            if (k === tabId || k.startsWith(`${tabId}-`)) {
                delete updateData[k];
            }
        });

        this.editDataTempByTav.next(updateData);
    }

    /**
     * Save active tab index for a record
     */
    saveActiveTabIndex(tabId: string, tabIndex: number) {
        try {
            const key = `activeTab_${tabId}`;
            localStorage.setItem(key, tabIndex.toString());
        } catch (error) {
            console.error('Error saving active tab index:', error);
        }
    }

    /**
     * Get active tab index for a record
     */
    getActiveTabIndex(tabId: string): number | null {
        try {
            const key = `activeTab_${tabId}`;
            const value = localStorage.getItem(key);
            return value !== null ? parseInt(value, 10) : null;
        } catch (error) {
            console.error('Error getting active tab index:', error);
            return null;
        }
    }

    /**
     * Clear active tab index for a record
     */
    clearActiveTabIndex(tabId: string) {
        try {
            const key = `activeTab_${tabId}`;
            localStorage.removeItem(key);
        } catch (error) {
            console.error('Error clearing active tab index:', error);
        }
    }

    /**
     * Save active tab index for creation mode (when creating new article)
     */
    saveActiveTabIndexForCreation(entityName: string, tabIndex: number) {
        try {
            const key = `activeTab_creation_${entityName}`;
            localStorage.setItem(key, tabIndex.toString());
        } catch (error) {
            console.error('Error saving active tab index for creation:', error);
        }
    }

    /**
     * Get active tab index for creation mode
     */
    getActiveTabIndexForCreation(entityName: string): number | null {
        try {
            const key = `activeTab_creation_${entityName}`;
            const value = localStorage.getItem(key);
            return value !== null ? parseInt(value, 10) : null;
        } catch (error) {
            console.error('Error getting active tab index for creation:', error);
            return null;
        }
    }

    /**
     * Clear active tab index for creation mode
     */
    clearActiveTabIndexForCreation(entityName: string) {
        try {
            const key = `activeTab_creation_${entityName}`;
            localStorage.removeItem(key);
        } catch (error) {
            console.error('Error clearing active tab index for creation:', error);
        }
    }

    /**
     * Clear all temporary data for creation mode
     */
    clearTempEditDataForCreation(entityName: string) {
        const updateData = { ...this.editDataTempByTav.value };
        const creationKey = `creation_${entityName}`;

        // Delete all creation-specific data
        Object.keys(updateData).forEach((k) => {
            if (k === creationKey || k.startsWith(`${creationKey}-`)) {
                delete updateData[k];
            }
        });

        this.editDataTempByTav.next(updateData);
    }

    /**
     * Save temporary data for creation mode
     */
    saveEditDataForCreation(entityName: string, data: any, tabIndex?: number) {
        const creationKey = `creation_${entityName}`;
        const key = tabIndex !== undefined ? `${creationKey}-${tabIndex}` : creationKey;
        const updateData = { ...this.editDataTempByTav.value, [key]: data };
        this.editDataTempByTav.next(updateData);
    }

    /**
     * Get temporary data for creation mode
     */
    getTempEditDataForCreation(entityName: string, tabIndex?: number): any {
        const creationKey = `creation_${entityName}`;
        if (tabIndex !== undefined) {
            const key = `${creationKey}-${tabIndex}`;
            return (
                this.editDataTempByTav.value?.[key] ??
                this.editDataTempByTav.value?.[creationKey] ??
                null
            );
        }
        return this.editDataTempByTav.value?.[creationKey] ?? null;
    }

    /**
     * Get all temporary data for creation mode (from all tabs)
     */
    getAllTempEditDataForCreation(entityName: string): any {
        const allData: any = {};
        const editData = this.editDataTempByTav.value || {};
        const creationKey = `creation_${entityName}`;

        // Get data from tab-specific keys
        Object.keys(editData).forEach((key) => {
            if (key.startsWith(`${creationKey}-`)) {
                const tabData = editData[key];
                if (tabData) {
                    Object.assign(allData, tabData);
                }
            }
        });

        // Also include data from creationKey if exists
        if (editData[creationKey]) {
            Object.assign(allData, editData[creationKey]);
        }

        return Object.keys(allData).length > 0 ? allData : null;
    }
}
